import type {
  FileSizeBucket,
  FileUploadDiagnostics,
  PhotoSlot,
  UploadProgress,
  PhotoUploadResult,
} from "./types";
import type { Id } from "@convex/_generated/dataModel";
import { convertHeicIfNeeded } from "./heic-conversion";
import { withDetectedImageType } from "./detect-image-type";
import { convertToStorableImage } from "./storable-format";
import { downscaleImageIfNeeded } from "./downscale";
import { validateImageFile, PhotoValidationError } from "./validation";
import { UPLOAD_TIMEOUT_MS } from "./constants";
import { readStorageId } from "@/lib/uploads/convex-file-upload";

export interface UploadOptions {
  file: File;
  slot: PhotoSlot;
  generateUploadUrl: () => Promise<string>;
  attestUpload: (storageId: Id<"_storage">) => Promise<unknown>;
  onProgress?: (progress: UploadProgress) => void;
  timeoutMs?: number;
}

export async function toStorablePhotoFormat(file: File): Promise<File> {
  const typedFile = await withDetectedImageType(file);
  return await convertToStorableImage(await convertHeicIfNeeded(typedFile));
}

export async function normalizePhotoFile(file: File): Promise<File> {
  return await downscaleImageIfNeeded(await toStorablePhotoFormat(file));
}

export async function uploadPhoto(
  options: UploadOptions,
): Promise<PhotoUploadResult> {
  const {
    file,
    slot,
    generateUploadUrl,
    attestUpload,
    onProgress,
    timeoutMs = UPLOAD_TIMEOUT_MS,
  } = options;

  let fileMetadata = getFileUploadDiagnostics(file);

  try {
    const processedFile = await normalizePhotoFile(file);
    fileMetadata = getFileUploadDiagnostics(processedFile);

    onProgress?.({ slot, status: "uploading", ...fileMetadata });

    const validation = await validateImageFile(processedFile);
    if (!validation.valid) {
      throw new PhotoValidationError(
        validation.error ?? "Image validation failed",
      );
    }

    const storageId = await uploadProcessedFileWithRetry(processedFile, {
      generateUploadUrl,
      timeoutMs,
    });
    await attestUpload(storageId);

    onProgress?.({ slot, status: "success", ...fileMetadata });

    return { storageId, slot };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Upload failed";
    onProgress?.({ slot, status: "error", error: message, ...fileMetadata });
    throw error;
  }
}

const MAX_UPLOAD_ATTEMPTS = 2;
const RETRY_BACKOFF_MS = 500;

function makeAbortError(): Error {
  const error = new Error("Upload cancelled");
  error.name = "AbortError";
  return error;
}

interface UploadAttemptOptions {
  generateUploadUrl: () => Promise<string>;
  timeoutMs: number;
}

async function postFileOnce(
  file: File,
  { generateUploadUrl, timeoutMs }: UploadAttemptOptions,
): Promise<{ response: Response; storageId?: Id<"_storage"> }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await abortableUpload(file, generateUploadUrl, controller.signal);
  } finally {
    clearTimeout(timeoutId);
  }
}

async function abortableUpload(
  file: File,
  generateUploadUrl: () => Promise<string>,
  signal: AbortSignal,
): Promise<{ response: Response; storageId?: Id<"_storage"> }> {
  let abort = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    abort = () => reject(makeAbortError());
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
  });
  const work = async () => {
    const uploadUrl = await generateUploadUrl();
    if (signal.aborted) throw makeAbortError();
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: { "Content-Type": file.type },
      body: file,
      signal,
    });
    return { response, storageId: response.ok ? await readStorageId(response) : undefined };
  };
  try {
    return await Promise.race([work(), interrupted]);
  } finally {
    signal.removeEventListener("abort", abort);
  }
}

async function backOffBeforeRetry(attempt: number): Promise<void> {
  if (attempt < MAX_UPLOAD_ATTEMPTS) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS));
  }
}

export async function uploadProcessedFileWithRetry(
  file: File,
  options: UploadAttemptOptions,
): Promise<Id<"_storage">> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    let result: Awaited<ReturnType<typeof postFileOnce>>;
    try {
      result = await postFileOnce(file, options);
    } catch (error) {
      lastError = error;
      await backOffBeforeRetry(attempt);
      continue;
    }
    if (result.storageId) return result.storageId;
    const response = result.response;
    const httpError = new Error(`Upload failed: ${response.status} ${response.statusText}`);
    if (response.status < 500) throw httpError;
    lastError = httpError;
    await backOffBeforeRetry(attempt);
  }
  throw lastError instanceof Error ? lastError : new Error("Upload failed");
}

export function getFileUploadDiagnostics(file: File): FileUploadDiagnostics {
  return {
    fileSize: file.size,
    fileSizeBucket: getFileSizeBucket(file.size),
    fileType: file.type || "unknown",
    ...getFileExtension(file.name),
  };
}

function getFileSizeBucket(fileSize: number): FileSizeBucket {
  const oneMb = 1024 * 1024;

  if (fileSize < oneMb) return "0-1mb";
  if (fileSize < 5 * oneMb) return "1-5mb";
  if (fileSize < 10 * oneMb) return "5-10mb";
  if (fileSize < 25 * oneMb) return "10-25mb";

  return "25mb-plus";
}

function getFileExtension(
  fileName: string,
): Pick<FileUploadDiagnostics, "fileExtension"> {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (!extension || extension === fileName.toLowerCase()) return {};
  if (!/^[a-z0-9]{1,8}$/.test(extension)) return {};

  return { fileExtension: extension };
}


export class Semaphore {
  private permits: number;
  private waiting: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire(): Promise<void> {
    if (this.permits > 0) {
      this.permits--;
      return;
    }

    return new Promise((resolve) => {
      this.waiting.push(resolve);
    });
  }

  release(): void {
    const next = this.waiting.shift();
    if (next) {
      next();
    } else {
      this.permits++;
    }
  }
}
